import 'src/hooks/howlerConfig'
import type { Howl } from 'howler'
import { createHowl } from './createHowl'

/**
 * Birdie Time's own cues (B23), one small standalone file per cue — the shape
 * the lightning strike and the crash bust already use — rather than a second
 * sprite sheet: a cue the owner wants swapped is one file, not a rebuilt sheet.
 *
 * Every file is trimmed and peak-normalised to −3 dB; the per-cue `volume` is
 * the mix under the effects channel and is what keeps them "nice, not
 * overpowering" (owner, 2026-09-21): a tick sits well under a chip, the
 * gallery under the win pill. TUNABLE by ear, here only.
 *
 * No tee-off "go" and no loss cue (owner 2026-09-21, second listen). No frost or fire cue (owner 2026-09-21): the skin lands on the socket row while the
 * viewer's picture is still seconds behind, so a widget-side cue is always out of sync
 * with the green. Those two belong in the clip audio, at render, if we do them at all.
 *
 * The Howls are built on demand (`preloadBirdieEffects`, called by the board
 * on mount), not at module scope: this module is imported by the shared
 * effects player, which every game mounts, and only Birdie should pay for
 * nine decodes.
 */
export const BIRDIE_EFFECTS = {
  /** race_ready — the board unlocks. Subtle (owner). */
  birdieRoundOpen: { file: 'birdie_round_open', volume: 0.22 },
  /** each 3 · 2 · 1 beat of the tee-off pill. Barely there (owner). */
  birdieTick: { file: 'birdie_tick', volume: 0.18 },
  /** a holed ring is drawn — a real ball dropping into the cup, a soft hollow drop with a brief
   *  rattle, peaked 5 dB under the others and mixed low: it plays up to three times a round and
   *  must stay subtle (owner, fourth listen: "a ball falling into a hole but very subtle"). */
  birdiePuttHoled: { file: 'birdie_putt_holed', volume: 0.3 },
  /** a missed ring is drawn — the soft bloop, peaked 5 dB under the rest and mixed low (owner: subtler still). */
  birdiePuttMissed: { file: 'birdie_putt_missed', volume: 0.25 },
  /** the settlement is on screen and the player won. A loss has no cue, like every other game
   *  (the shared panel plays nothing on a loss either; its voice-over is suppressed on Birdie). */
  birdieWin: { file: 'birdie_win', volume: 0.45 },
  /** a big win, twice on a mega win, just before the total — Birdie's own, because the shared
   *  bonus chime is the level-up sound (owner 2026-09-21). */
  birdieBigWin: { file: 'birdie_big_win', volume: 0.5 }, // 4.5 s with its own tail — the shorter cut felt cut off (owner)
  /** all three dropped — a small songbird after the third ball's own drop. A detail, mixed low (the boss). */
  birdieBirdie: { file: 'birdie_birdie', volume: 0.22 },
  /** all three dropped on a hard card (make chance ≤ 50%) — the rarer, bigger bird. */
  birdieEagle: { file: 'birdie_eagle', volume: 0.26 }
} as const satisfies Record<string, { file: string; volume: number }>

export type BirdieEffectType = keyof typeof BIRDIE_EFFECTS

export const BIRDIE_EFFECT_TYPES = Object.keys(BIRDIE_EFFECTS) as BirdieEffectType[]

export const isBirdieEffect = (type: string): type is BirdieEffectType => type in BIRDIE_EFFECTS

/**
 * Bump whenever a file under effects/birdie/ changes. The files keep their names when a take is
 * swapped, and a browser (and the CDN) that heard the old one keeps serving it — the owner heard
 * round one's cues after round five had shipped. Same idea as the music's SOUNDS_CACHE_BUSTER,
 * but scoped to these files so a Birdie retune never refetches every game's music.
 */
export const BIRDIE_SOUNDS_VERSION = '2026-09-22a'

export const birdieEffectSources = (type: BirdieEffectType): string[] => [
  `/assets/sounds/effects/birdie/${BIRDIE_EFFECTS[type].file}.mp3?v=${BIRDIE_SOUNDS_VERSION}`,
  `/assets/sounds/effects/birdie/${BIRDIE_EFFECTS[type].file}.aac?v=${BIRDIE_SOUNDS_VERSION}`
]

const howls = new Map<BirdieEffectType, Howl>()
// The last channel level the player applied, so a Howl built after it was set
// starts at the right level rather than at 1.
let channel = { volume: 1, muted: false }

const levelFor = (type: BirdieEffectType): number =>
  channel.muted ? 0 : channel.volume * BIRDIE_EFFECTS[type].volume

function howlFor(type: BirdieEffectType): Howl {
  const existing = howls.get(type)
  if (existing) return existing
  const howl = createHowl({
    autoplay: false,
    preload: true,
    src: birdieEffectSources(type),
    volume: levelFor(type)
  })
  howls.set(type, howl)
  return howl
}

/** Build (and start loading) every cue — the board calls this once on mount. */
export function preloadBirdieEffects(): void {
  for (const type of BIRDIE_EFFECT_TYPES) howlFor(type)
}

export function playBirdieEffect(type: BirdieEffectType): void {
  howlFor(type).play()
}

/** The effects channel level, applied under each cue's own mix. */
export function setBirdieEffectsLevel(volume: number, muted: boolean): void {
  channel = { volume, muted }
  for (const [type, howl] of howls) howl.volume(levelFor(type))
}

/** Stop everything (tab hidden) — a cue must never replay on refocus. */
export function stopBirdieEffects(): void {
  for (const howl of howls.values()) howl.stop()
}

/** Test seam: forget the built Howls. */
export function resetBirdieEffectsForTests(): void {
  howls.clear()
  channel = { volume: 1, muted: false }
}
